<template>
  <div class="c-form-group">
    <div v-if="mainSection" class="main">
      <c-title v-if="mainSection.title" dark-text>
        {{ mainSection.title }}
      </c-title>

      <c-form-builder
        ref="mainForm"
        class="form"
        no-actions
        show-overflow
        no-bottom-shadow
        no-upper-shadow
        :disabled="disabled"
        :label-left="labelLeft"
        :fields-options="mainFieldsOptions"
        :schema="mainSection.fields"
        :value="mainForm"
        @input="setMainForm"
      >
        <slot name="main-section" />

        <slot
          slot="footer"
          name="main-footer"
        />

        <slot
          slot="actions"
          name="main-actions"
        />
      </c-form-builder>
    </div>

    <div class="groups">
      <div
        v-for="group in groups"
        :key="group.groupName"
        class="group"
      >
        <c-title
          v-if="group.groupLabel"
          class="title"
          dark-text
        >
          {{ group.groupLabel }}
        </c-title>

        <div class="forms">
          <div
            v-for="(form, index) in group.forms"
            :key="`${group.groupName}-form-${index}`"
            class="form-wrapper"
          >
            <div class="heading" :class="{ '-full-width': group.fullWidthHeader }">
              <c-flag
                :icon="group.headerIcon"
                class="flag"
                size="40"
                icon-size="18"
                grey
              />

              <span class="label">
                {{ index + 1 }}{{ group.numeralLetter || 'ª'}} {{ group.headerLabel }}
              </span>

              <transition name="fade">
                <c-button
                  v-if="isItemDeletable(group)"
                  class="btn-delete"
                  flat
                  error
                  icon="trash"
                  icon-size="18"
                  size="30"
                  :disabled="group.disabled"
                  @click="handleDeleteGroupItem(group, index)"
                />
              </transition>

              <slot
                :name="`${group.groupName}-delete-modal`"
                :attrs="{ isOpened: showDeleteModal(group.groupName, index), form }"
                :listeners="{
                  cancel: () => { isDeletingGroupItem = null },
                  delete: () => { deleteGroupItem(...isDeletingGroupItem) },
                }"
              />
            </div>

            <c-form-builder
              ref="groupForms"
              class="form"
              no-actions
              show-overflow
              no-bottom-shadow
              no-upper-shadow
              :label-left="labelLeft"
              :schema="group.fields"
              :fields-options="getFieldOptions(group, form)"
              :disabled="disabled || group.disabled"
              :value="form"
              @input="setGroupForm(group, index, $event)"
            >
              <template
                v-for="slot in group.slots"
                :slot="slot"
                slot-scope="fieldProps"
              >
                <slot
                  :name="`${group.groupName}-${slot}`"
                  v-bind="fieldProps"
                />
              </template>

              <template v-for="(_, fieldName) in group.fields">
                <slot
                  :slot="`${fieldName}-divider`"
                  :name="`${group.groupName}-${fieldName}-divider`"
                />
              </template>

              <slot :name="group.groupName" />

              <slot
                slot="footer"
                :name="`${group.groupName}-footer`"
              />

              <slot
                slot="actions"
                :name="`${group.groupName}-actions`"
              />
            </c-form-builder>
          </div>
        </div>

        <c-add
          class="add-box"
          :text="group.addBtnLabel"
          :icon-only="!group.addBtnLabel"
          :mobile-icon-only="!group.addBtnLabel"
          :disabled="group.disabled || shouldDisableAddButton(group)"
          @add="addFormToGroup(group)"
        />

        <c-validation-message
          v-if="!!groupsValidationMessage[group.groupName]"
          :key="`validation-message-${group.groupName}`"
          class="validation-message"
        >
          {{ group.maxItemsMessage }}
        </c-validation-message>
      </div>
    </div>

    <slot name="after-groups" />

    <div v-if="!noActions" class="actions">
      <slot name="actions">
        <c-button primary class="action">
          Salvar
        </c-button>
      </slot>
    </div>
  </div>
</template>

<script>
import CFormBuilder from '../CFormBuilder/index.vue'
import CTitle from '../CTitle/index.vue'
import CFlag from '../CFlag/index.vue'
import CAdd from '../CAdd/index.vue'

export default {
  name: 'CFormGroup',

  components: {
    CFormBuilder,
    CTitle,
    CFlag,
    CAdd
  },

  props: {
    schema: {
      type: Object,
      required: true
    },

    value: {
      type: Object,
      default: () => ({})
    },

    mainFieldsOptions: {
      type: Object,
      default: () => ({})
    },

    fieldsOptions: {
      type: Object,
      default: () => ({})
    },

    labelLeft: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false
    },

    noActions: {
      type: Boolean,
      default: false
    },
  },

  data: () => ({
    groupsValidationMessage: {},
    isDeletingGroupItem: null,
  }),

  computed: {
    formData: {
      get () {
        return this.value
      },
      set (newData) {
        this.emitInput(newData)
      }
    },

    mainSection () {
      return (this.schema || {}).main || null
    },

    mainForm () {
      if (!this.mainSection) return {}

      const mainFields = Object.keys(this.mainSection.fields || {})
      return mainFields.reduce((acc, fieldName) => {
        if (this.formData[fieldName] !== undefined) {
          acc[fieldName] = this.formData[fieldName]
        }
        return acc
      }, {})
    },

    groups () {
      const groups = (this.schema || {}).groups || []

      return groups.map(group => ({
        ...group,
        valuePath: `formData[${group.groupName}]`,
        forms: this.getGroupForms(group),
        slots: Object
          .entries((group || {}).fields)
          .filter(([ , field ]) => field.type === 'slot')
          .map(([ fieldName ]) => fieldName + '-slot')
      }))
    },
  },

  methods: {
    emitInput (payload) {
      this.$emit('input', payload)
    },

    getGroupForms (group) {
      this.formData[group.groupName] = this.formData[group.groupName] || []
      if (this.formData[group.groupName].length) return this.formData[group.groupName]

      if (group.startWithForm) {
        this.formData[group.groupName] = [ {} ]
      }

      return this.formData[group.groupName]
    },

    getFieldOptions (group, form) {
      const { uniqueFieldOptions, groupName, fields = {} } = group
      const { fieldsOptions } = this || {}

      if (!uniqueFieldOptions) return fieldsOptions

      const uniqueFields = Object.entries(fields).reduce((acc, entry) => {
        if (entry[1]?.uniqueFieldOptions)
          acc.push({
            field: entry[0], optionsSrc: entry[1]?.optionsSrc, trackBy: entry[1]?.trackBy
          })

        return acc
      }, []);

      const groupForms = this.formData[groupName] || []

      const options = Object.entries(fieldsOptions).reduce((acc, option) => {
        const relatedFields = uniqueFields.filter(({ optionsSrc }) => option[0] === optionsSrc)

        if (!relatedFields.length) {
          const [ _, optionData ] = option

          acc[option[0]] = optionData
          return acc
        }

        // collect all used values from all fields that use this options source
        const allUsedValues = new Set()

        groupForms.forEach(formData => {
          relatedFields.forEach(({ field }) => {
            const value = formData[field]
            if (value != null) {
              allUsedValues.add(value)
            }
          })
        })

        // current form values must be allowed
        const currentFormValues = new Set()
        relatedFields.forEach(({ field }) => {
          const value = form[field]
          if (value != null) {
            currentFormValues.add(value)
          }
        })

        acc[option[0]] = option[1].filter(({ [relatedFields[0].trackBy]: trackBy }) => {
          return currentFormValues.has(trackBy) || !allUsedValues.has(trackBy)
        })

        return acc
      }, {})

      return {
        ...options,
      }
    },

    shouldDisableAddButton (group) {
      // if there is no uniqueFieldOptions flag active, do not disable
      if (!group.uniqueFieldOptions) return false

      const { fields = {}, groupName } = group

      // search for fields that have shouldBlockAddButtonIfEmpty
      const fieldsToCheck = Object.entries(fields).filter(([ , fieldConfig ]) =>
        fieldConfig.shouldBlockAddButtonIfEmpty && fieldConfig.uniqueFieldOptions
      )

      // if there are no fields to check, do not disable
      if (!fieldsToCheck.length) return false

      // get existing forms in the group
      const currentGroupData = this.formData[groupName] || []
      const totalFormsCount = currentGroupData.length

      // for each field that should block, check if there are available options
      return fieldsToCheck.some(([ , fieldConfig ]) => {
        const { optionsSrc } = fieldConfig
        if (!optionsSrc || !this.fieldsOptions || !this.fieldsOptions[optionsSrc]) {
          return false
        }

        // get the total number of unique options available
        const totalUniqueOptions = this.fieldsOptions[optionsSrc]?.length || 0

        // if we already have as many forms as unique options, disable
        if (totalFormsCount >= totalUniqueOptions) {
          return true
        }

        // simulate an empty form to check available options
        const emptyForm = {}
        const availableOptions = this.getFieldOptions(group, emptyForm)

        return !availableOptions[optionsSrc] || !availableOptions[optionsSrc].length
      })
    },

    async onSubmit () {
      const { mainForm, groupForms } = this.$refs
      const allForms = [
        ...(this.mainSection && mainForm ? [ mainForm ] : []),
        ...groupForms || []
      ]

      const isValid = (await Promise.all(allForms
        .map(form => form.onSubmit()))).every(Boolean)

      if (isValid) this.$emit('submit', this.formData)

      return this.formData
    },

    async onValidate () {
      const { mainForm, groupForms } = this.$refs
      const allForms = [
        ...(this.mainSection && mainForm ? [ mainForm ] : []),
        ...groupForms || []
      ]

      const isValid = (await Promise.all(allForms
        .map(form => form.onValidate()))).every(Boolean)

      return isValid
    },

    handleDeleteGroupItem (group, index) {
      if (!this.$scopedSlots[`${group.groupName}-delete-modal`]) {
        this.deleteGroupItem(group, index)
        return
      }

      this.isDeletingGroupItem = [ group, index ]

      const { [group.groupName]: _, ...rest } = this.groupsValidationMessage
      this.groupsValidationMessage = { ...rest }
    },

    showDeleteModal (groupName, index) {
      return (this.isDeletingGroupItem || [])[0]?.groupName === groupName
        && (this.isDeletingGroupItem || [])[1] === index
    },

    deleteGroupItem (group = {}, index) {
      const currentForms = this.formData[group.groupName] || []
      if (!currentForms[index]) return

      const newForms = [ ...currentForms ]
      newForms.splice(index, 1)

      const newFormData = {
        ...this.formData,
        [group.groupName]: newForms
      }

      this.emitInput(newFormData)

      this.isDeletingGroupItem = null
    },

    addFormToGroup (group) {
      const { maxItems, groupName } = group;
      const currentGroupData = this.formData[groupName] || [];

      if (!maxItems || currentGroupData.length < maxItems) {
        const newFormData = {
          ...this.formData,
          [groupName]: [ ...currentGroupData, {} ]
        }

        this.emitInput(newFormData)

        const { [groupName]: _, ...rest } = this.groupsValidationMessage
        this.groupsValidationMessage = { ...rest }
      } else {
        this.groupsValidationMessage = {
          ...this.groupsValidationMessage,
          [groupName]: true
        }
      }
    },

    setMainForm (data) {
      const newFormData = { ...this.formData }

      if (this.mainSection) {
        const mainFields = Object.keys(this.mainSection.fields || {})
        mainFields.forEach(fieldName => {
          if (data[fieldName] !== undefined) {
            newFormData[fieldName] = data[fieldName]
          }
        })
      }

      this.emitInput(newFormData)
    },

    isItemDeletable (group) {
      const minItems = Math.max(group.requiredItems || 0, group.startWithForm ? 1 : 0)

      return group.forms?.length > minItems
    },

    setGroupForm (group, index, data) {
      const currentGroupData = this.formData[group.groupName] || []
      const newGroupData = [ ...currentGroupData ]
      newGroupData[index] = data

      const newFormData = {
        ...this.formData,
        [group.groupName]: newGroupData
      }

      this.emitInput(newFormData)
    }
  }
}
</script>

<style lang="scss">
.c-form-group {
  @include responsive (xs-mobile, mobile) {
    & > .main { margin-top: -10px; }
  }

  & > .groups > .group {
    @include responsive (xs-mobile, mobile) { margin-top: 10px; }

    & > .title {
      margin: 30px 0px 20px 0px;

      @include responsive (tablet, desktop) { margin-bottom: 30px; }
    }

    & > .forms > .form-wrapper {
      padding-bottom: 10px;
      margin-bottom: 30px;
      border-bottom: 1px solid color-var(text, base-10);

      @include responsive (tablet, desktop) {
        margin-bottom: 50px;
        padding-bottom: 40px;
      }
    }

    & > .forms > .form-wrapper > .heading {
      display: flex;
      align-items: center;

      padding-bottom: 10px;

      // The heading area has to occupy the same visual width
      // as the other fields, ideally this shouldn't be a
      // hardcoded value but there's no way around it currently
      @include responsive (tablet, desktop) {
        width: 570px;

        &.-full-width {
          width: auto;
        }
      }

      & > .label { @include typo(h2); margin-left: 10px; }
      & > .btn-delete { margin-left: auto; }

      & > .fade-enter-active,
      & > .fade-leave-active {
        transition: opacity 0.3s ease;
      }

      & > .fade-enter,
      & > .fade-leave-to {
        opacity: 0;
      }
    }

    & > .add-box {
      height: auto;
      padding: 30px 0;
    }

    & > .validation-message {
      padding-top: 20px;
    }
  }

  & > .actions {
    display: flex;
    justify-content: center;
    align-items: center;

    padding: 40px 0 50px 0;

    & > .action {
      width: 100%;
      max-width: 180px;
    }
  }
}
</style>
